import { AfterViewInit, DestroyRef, Directive, ElementRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';

/**
 * Renders a money input's own value to two decimal places when it loses focus, so `2500`
 * becomes `2500.00` — and, on a text input, groups it to thousands as well: `203280.61`
 * becomes `203,280.61`.
 *
 * Purely presentational. A dollar box that reads `2500` next to columns of `$2,500.00` looks
 * like a different kind of figure, and the advisor is being asked to check totals across them.
 *
 * ⚠️ This is NOT rounding, and must not be relied on as any. `2500` and `2500.00` are the same
 * number, so the control's value is untouched and no `input`/`change` event is dispatched --
 * which is what keeps it from re-entering the store or looping through change detection.
 * Whatever needs the stored figure held to whole cents does that itself (`round2` in the store);
 * a display directive is the wrong place to enforce it, because a value can reach the model
 * without ever passing through a blur.
 *
 * **Empty stays empty.** Both boxes this is used on treat a blank as "nothing typed yet" and
 * show a red hint beside it; filling in `0.00` would dress that up as an answer. Same reason
 * `pctInput`/`valueInput` on the allocation grid render zero as blank.
 *
 * **Unparseable stays as typed.** If the box somehow holds text, the advisor sees what they
 * wrote rather than having it silently replaced -- validation is elsewhere and can say so.
 *
 * 🟢 Works on `type="number"`, which is why neither box had to change type: `"2500.00"` is a
 * valid floating-point number string, so the value sanitiser keeps it and the browser shows it.
 * Leaving them numeric also keeps the browser's own rejection of thousands separators, which a
 * text input would have given up -- a pasted `2,500.00` would otherwise parse as `2`.
 *
 * ⚠️ **GROUPING IS THEREFORE GATED ON THE INPUT TYPE**, and that is not a preference. A
 * `type="number"` input runs its value through a sanitiser that discards anything which is not a
 * plain floating-point string, so assigning `"203,280.61"` to one leaves it EMPTY -- the figure
 * would silently vanish as the advisor tabbed out. Only `type="text"` boxes get separators; the
 * numeric ones keep the padding they always had.
 *
 * ⚠️ A text box that gets separators must have every parse of it strip them first. In this
 * wizard that is `onPot` and `onValue` on the allocation step, both of which do -- and note that
 * a REACTIVE control bound to such a box would take the grouped STRING as its value, so anything
 * doing arithmetic on it needs the same treatment. Nothing does today.
 *
 * ⚠️ Needed **in addition to** formatting the `[value]` binding, not instead of it. The binding
 * covers a real edit: `change` fires, the store updates, and the re-render writes an already
 * formatted string. It does nothing when the advisor retypes the SAME figure -- no `change`
 * event, so no re-render, so the raw `2500` they just typed stays on screen. That is the case
 * this catches, and on a reactive control with no value binding of its own (the protected-cash
 * box) it is the only mechanism there is.
 *
 * ⚠️ **BLUR IS NOT THE ONLY WAY A FIGURE REACHES THE BOX**, which is what this got wrong. A
 * reactive control writes its value into the DOM whenever the view is built or the model
 * changes, and neither of those is a blur -- so `3479.9` formatted correctly the first time the
 * advisor tabbed out, then came back as `3479.9` the moment they left the step and returned.
 * The control holds a NUMBER, and 3479.90 is not a number; the trailing zero only ever existed
 * on screen, so anything that re-renders from the model loses it.
 *
 * Two more triggers close that:
 *
 *   - after the view initialises, for a box arriving with a value already in it
 *   - on `valueChanges`, for a figure the STORE put there rather than the advisor -- the
 *     use-all-cash sweep and Allocate Remaining both do this
 *
 * ⚠️ Both are skipped while the box HAS FOCUS. Rewriting an input mid-keystroke moves the caret
 * and would overwrite a half-typed `3479.` with `3479.00`, which is the one thing worse than the
 * bug being fixed.
 *
 * Standalone so it can be imported directly by standalone components.
 */
@Directive({
  selector: '[appTwoDecimals]',
  standalone: true,
})
export class TwoDecimalsDirective implements AfterViewInit {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  /**
   * Optional and self-scoped: the directive is also used on plain inputs with no reactive
   * control, where there is nothing to subscribe to and nothing to fix -- those are written by a
   * `[value]` binding the template already formats.
   */
  private readonly control = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    // ⚠️ AFTER the view, not on init: the forms machinery writes the control's value into the
    // DOM as part of view setup, so formatting any earlier reads an empty box and does nothing.
    this.formatIfNotFocused();

    this.control?.valueChanges
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formatIfNotFocused());
  }

  @HostListener('blur')
  onBlur(): void {
    // ⚠️ UNGUARDED, unlike the two model-driven paths. Blur means the advisor has finished
    // with this box, so there is no caret to protect -- and a `blur` EVENT does not necessarily
    // mean focus has moved, which is exactly what a synthetic one in a spec does not do. Routing
    // this through the focus guard silently disabled the original behaviour.
    this.format();
  }

  /**
   * Format only when the advisor is not in the box. `valueChanges` fires on every keystroke for
   * a control that is not `updateOn: 'blur'`, and rewriting the value then jumps the caret to
   * the end -- turning a half-typed `3479.` into `3479.00` under their fingers.
   */
  private formatIfNotFocused(): void {
    const input = this.el.nativeElement;
    if (input.ownerDocument.activeElement === input) return;
    this.format();
  }

  /**
   * ⚠️ Writes `input.value` directly and dispatches NOTHING, which is what keeps this off the
   * change-detection treadmill: `valueChanges` cannot re-enter itself, because padding a
   * displayed string is not a change to the control.
   */
  private format(): void {
    const input = this.el.nativeElement;

    // ⚠️ Separators stripped BEFORE parsing, because this now writes them: on the second blur of
    // an untouched text box the value being read back is already grouped, and `parseFloat` stops
    // at the comma -- so `203,280.61` would come back as 203 and the box would rewrite itself to
    // `203.00`.
    const raw = input.value.trim().replace(/,/g, '');
    if (raw === '') return;

    const value = Number.parseFloat(raw);
    if (!Number.isFinite(value)) return;

    input.value = input.type === 'number'
      ? value.toFixed(2)
      : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
